/**
 * Public Learning Package Detail Page
 * Presents the package as a guided journey: a personal progress bar with rank,
 * the rich description, three sectioned content areas with completion badges,
 * and a personal dashboard (timeline + quiz strengths/weaknesses).
 * Works identically for anonymous visitors (visitor key) and logged-in users.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Col, Drawer, Progress, Row, Spin, Tag, Typography, message } from 'antd';
import {
  ApartmentOutlined,
  AppstoreAddOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  ExperimentOutlined,
  EyeOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RocketOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import PackageTimeline from '../../../components/PackageTimeline';
import PagesWrapper from '../../../PagesWrapper';
import { useAuth } from '../../../context/AuthContext';
import {
  getMyPackageStats,
  getPublicLearningPackage,
  recordPackageItemOpen,
  recordPackageVisit,
  PackageItemTypes,
} from '../../../services/learningPackagesService';
import styles from './LearningPackagesPublic.module.css';

const { Title, Text } = Typography;

const itemKeyOf = (itemType, itemId) => `${itemType}-${itemId}`;

/**
 * Renders the public learning package page with the personal journey dashboard.
 * @returns {JSX.Element} Public package detail page
 */
const PackagePublicDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const packageId = parseInt(id, 10);

  const [pkg, setPkg] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const visitRecordedRef = useRef(false);

  const loadPackage = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPublicLearningPackage(packageId, { suppressNotification: true });
      setPkg(result);
    } catch (error) {
      message.error(t('learningPackages.publicFetchError', 'Failed to load learning packages'));
    } finally {
      setLoading(false);
    }
  }, [packageId, t]);

  const loadMyStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const result = await getMyPackageStats(packageId, { suppressNotification: true });
      setMyStats(result);
    } catch (error) {
      // The dashboard is optional enrichment; the package page still works without it.
      setMyStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    loadPackage();
    loadMyStats();
  }, [loadPackage, loadMyStats]);

  useEffect(() => {
    if (visitRecordedRef.current || !packageId) return;
    visitRecordedRef.current = true;
    recordPackageVisit(packageId, { suppressNotification: true }).catch(() => {});
  }, [packageId]);

  const myItemsByKey = useMemo(() => {
    const map = new Map();
    (myStats?.items || []).forEach(item => map.set(itemKeyOf(item.itemType, item.itemId), item));
    return map;
  }, [myStats]);

  // The first incomplete item across areas (paths, then quizzes, then profiles) gets the
  // "Continue here" highlight so the page reads as a guided journey.
  const continueKey = useMemo(() => {
    if (!pkg) return null;
    const orderedItems = [
      ...(pkg.paths || []),
      ...(pkg.quizCollections || []),
      ...(pkg.trainingProfiles || []),
    ];
    const firstIncomplete = orderedItems.find(item => {
      const mine = myItemsByKey.get(itemKeyOf(item.itemType, item.itemId));
      return !mine?.completed;
    });
    return firstIncomplete ? itemKeyOf(firstIncomplete.itemType, firstIncomplete.itemId) : null;
  }, [pkg, myItemsByKey]);

  const handleOpenItem = (item) => {
    // Fire-and-forget analytics; profile completion derivation depends on this event.
    recordPackageItemOpen(packageId, item.itemType, item.itemId, { suppressNotification: true }).catch(() => {});

    switch (item.itemType) {
      case PackageItemTypes.LearningPath:
        navigate(`/learning-path/${item.itemId}`);
        break;
      case PackageItemTypes.QuizCollection:
        navigate(`/quiz/${item.itemId}`);
        break;
      case PackageItemTypes.TrainingProfile:
        navigate(`/simulations/heat-pump-training?profileId=${item.itemId}`);
        break;
      default:
        break;
    }
  };

  const renderItemCard = (item, openLabel) => {
    const key = itemKeyOf(item.itemType, item.itemId);
    const mine = myItemsByKey.get(key);
    const isContinue = key === continueKey;
    const completed = mine?.completed;

    const subQuizzes = mine?.subQuizzes || [];
    const completedSubQuizzes = subQuizzes.filter(q => q.completed).length;
    const questProgress = mine?.questProgress;
    const hasDrawerDetails = subQuizzes.length > 0 || (questProgress?.totalQuestCount ?? 0) > 0 || (questProgress?.quests?.length ?? 0) > 0;

    return (
      <Col xs={24} sm={12} lg={8} key={key}>
        <Card
          className={[
            styles.itemCard,
            completed ? styles.itemCardCompleted : '',
            isContinue ? styles.itemCardContinue : '',
          ].join(' ')}
          onClick={() => handleOpenItem(item)}
        >
          {isContinue && (
            <Tag color="blue" icon={<RocketOutlined />} className={styles.continueBadge}>
              {t('learningPackages.continueHere', 'Continue here')}
            </Tag>
          )}
          <div className={styles.itemCardHeader}>
            <span className={styles.itemOrderBadge}>{item.orderIndex + 1}</span>
            <span className={styles.itemName}>{item.name}</span>
            {completed && <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />}
          </div>
          <div className={styles.itemMeta}>
            {completed && <Tag color="green">{t('learningPackages.completedBadge', 'Completed')}</Tag>}
            {mine?.bestScorePercent != null && (
              <Tag color="gold" icon={<TrophyOutlined />}>
                {t('learningPackages.bestScore', 'Best score')}: {mine.bestScorePercent}%
              </Tag>
            )}
            {mine?.attemptCount > 0 && (
              <span>{t('learningPackages.attemptsLabel', 'Attempts')}: {mine.attemptCount}</span>
            )}
          </div>
          {subQuizzes.length > 0 && (
            <div className={styles.subProgressRow}>
              <span className={styles.subProgressLabel}>
                <QuestionCircleOutlined />
                {t('learningPackages.subQuizProgress', {
                  completed: completedSubQuizzes,
                  total: subQuizzes.length,
                  defaultValue: 'Quizzes inside: {{completed}}/{{total}}',
                })}
              </span>
              <Progress
                className={styles.subProgressBar}
                percent={subQuizzes.length > 0 ? Math.round((completedSubQuizzes / subQuizzes.length) * 100) : 0}
                size="small"
                showInfo={false}
              />
            </div>
          )}
          {questProgress && questProgress.quests?.length > 0 && (
            <div className={styles.subProgressRow}>
              <span className={styles.subProgressLabel}>
                <TrophyOutlined />
                {t('learningPackages.questProgress', {
                  completed: questProgress.completedQuestCount,
                  total: questProgress.totalQuestCount,
                  defaultValue: 'Quests: {{completed}}/{{total}}',
                })}
              </span>
              <Progress
                className={styles.subProgressBar}
                percent={questProgress.totalQuestCount > 0
                  ? Math.round((questProgress.completedQuestCount / questProgress.totalQuestCount) * 100)
                  : 0}
                size="small"
                showInfo={false}
              />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <Button type="link" style={{ padding: 0 }}>{openLabel}</Button>
            {hasDrawerDetails && (
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  setDetailItem({ ...item, mine });
                }}
              >
                {t('learningPackages.detailsButton', 'Details')}
              </Button>
            )}
          </div>
        </Card>
      </Col>
    );
  };

  const renderArea = (icon, title, items, openLabel) => {
    if (!items || items.length === 0) return null;
    return (
      <div className={styles.areaSection}>
        <div className={styles.areaSectionTitle}>
          <span className={styles.areaSectionIcon}>{icon}</span>
          {title}
        </div>
        <Row gutter={[16, 16]}>
          {items.map(item => renderItemCard(item, openLabel))}
        </Row>
      </div>
    );
  };

  const sortedQuizPerformance = useMemo(
    () => [...(myStats?.quizPerformance || [])].sort((a, b) => b.averageScorePercent - a.averageScorePercent),
    [myStats]
  );

  const hasActivity = (myStats?.timeline || []).length > 0;

  return (
    <PagesWrapper>
      <Spin spinning={loading}>
        <div className={styles.container}>
          {pkg && (
            <>
              <div className={styles.header}>
                <div className={styles.titleSection}>
                  <AppstoreAddOutlined className={styles.titleIcon} />
                  <Title level={2} className={styles.title}>{pkg.name}</Title>
                  {pkg.countryLanguage && (
                    <Tag icon={<GlobalOutlined />}>{pkg.countryLanguage}</Tag>
                  )}
                </div>
              </div>

              <Card className={styles.journeyCard}>
                <div className={styles.journeyHeader}>
                  <h3 className={styles.journeyTitle}>
                    <RocketOutlined /> {t('learningPackages.journeyTitle', 'Your Journey')}
                  </h3>
                  <div className={styles.journeyMeta}>
                    {myStats?.rank && (
                      <span className={styles.rankChip}>
                        <TrophyOutlined />
                        {t('learningPackages.journeyRank', {
                          position: myStats.rank.position,
                          total: myStats.rank.totalParticipants,
                          defaultValue: "You're #{{position}} of {{total}} participants",
                        })}
                      </span>
                    )}
                    <Button size="small" icon={<ReloadOutlined />} loading={statsLoading} onClick={loadMyStats}>
                      {t('common.refresh', 'Refresh')}
                    </Button>
                  </div>
                </div>
                <div className={styles.journeyProgressRow}>
                  <Progress
                    type="circle"
                    size={72}
                    percent={Math.round(myStats?.progressPercent ?? 0)}
                  />
                  <div className={styles.journeyProgressBar}>
                    <Text strong>
                      {t('learningPackages.journeyCompletedOf', {
                        completed: myStats?.completedItemCount ?? 0,
                        total: myStats?.totalItemCount ?? 0,
                        defaultValue: '{{completed}} of {{total}} items completed',
                      })}
                    </Text>
                    <Progress percent={Math.round(myStats?.progressPercent ?? 0)} showInfo={false} />
                  </div>
                </div>
              </Card>

              {pkg.descriptionRichContent && (
                <Card className={styles.descriptionCard}>
                  <div
                    className={styles.descriptionContent}
                    // Rich text authored by content creators through the trusted editor pipeline.
                    dangerouslySetInnerHTML={{ __html: pkg.descriptionRichContent }}
                  />
                </Card>
              )}

              {renderArea(
                <ApartmentOutlined />,
                t('learningPackages.areaPaths', 'Learning Paths'),
                pkg.paths,
                t('learningPackages.openPath', 'Open learning path')
              )}
              {renderArea(
                <QuestionCircleOutlined />,
                t('learningPackages.areaQuizzes', 'Quiz Collections'),
                pkg.quizCollections,
                t('learningPackages.startQuiz', 'Start quiz')
              )}
              {renderArea(
                <ExperimentOutlined />,
                t('learningPackages.areaProfiles', 'Training Profiles'),
                pkg.trainingProfiles,
                t('learningPackages.openSimulation', 'Open simulation')
              )}

              <div className={styles.areaSectionTitle}>
                <span className={styles.areaSectionIcon}><ClockCircleOutlined /></span>
                {t('learningPackages.myDashboard', 'My Dashboard')}
              </div>
              <div className={styles.dashboardGrid}>
                <Card title={t('learningPackages.timelineTitle', 'Recent Activity')}>
                  {hasActivity ? (
                    <PackageTimeline groups={myStats?.timeline} maxGroups={8} maxEventsPerGroup={6} />
                  ) : (
                    <div className={styles.noActivity}>
                      <p>{t('learningPackages.noActivityYet', 'No activity yet')}</p>
                      <Text type="secondary">
                        {t('learningPackages.noActivityHint', 'Open items below to start your journey. Your progress is tracked on this device.')}
                      </Text>
                    </div>
                  )}
                </Card>
                <Card title={t('learningPackages.strengthsTitle', 'Strengths & Weaknesses')}>
                  {sortedQuizPerformance.length > 0 ? (
                    sortedQuizPerformance.map(quiz => (
                      <div key={quiz.quizCollectionId} className={styles.strengthRow}>
                        <div className={styles.strengthName}>
                          <span>{quiz.name}</span>
                          <span>{quiz.averageScorePercent}%</span>
                        </div>
                        <Progress
                          percent={quiz.averageScorePercent}
                          showInfo={false}
                          strokeColor={quiz.averageScorePercent >= 70 ? '#52c41a' : quiz.averageScorePercent >= 40 ? '#faad14' : '#ff4d4f'}
                        />
                      </div>
                    ))
                  ) : (
                    <div className={styles.noActivity}>
                      <p>{t('learningPackages.noActivityYet', 'No activity yet')}</p>
                    </div>
                  )}
                </Card>
              </div>

              {!isAuthenticated && (
                <Alert
                  type="info"
                  showIcon
                  className={styles.anonymousNotice}
                  message={t('learningPackages.anonymousNotice', 'Your progress is tracked anonymously in this browser. It does not require an account and is not a login.')}
                />
              )}
            </>
          )}
        </div>
      </Spin>

      <Drawer
        title={detailItem?.name}
        open={!!detailItem}
        onClose={() => setDetailItem(null)}
        width={420}
      >
        {detailItem?.mine?.subQuizzes?.length > 0 && (
          <>
            <Typography.Title level={5}>{t('learningPackages.subQuizzesTitle', 'Quizzes inside this path')}</Typography.Title>
            {detailItem.mine.subQuizzes.map(quiz => (
              <div key={quiz.quizCollectionId} className={styles.drawerItemRow}>
                {quiz.completed
                  ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                  : <ClockCircleOutlined style={{ color: '#d9d9d9', fontSize: 16 }} />}
                <div className={styles.drawerItemName}>
                  {quiz.name}
                  <div className={styles.drawerItemMeta}>
                    <span>{t('learningPackages.attemptsLabel', 'Attempts')}: {quiz.attemptCount}</span>
                    {quiz.bestScorePercent != null && (
                      <span>{t('learningPackages.bestScore', 'Best score')}: {quiz.bestScorePercent}%</span>
                    )}
                    {quiz.averageScorePercent != null && (
                      <span>{t('learningPackages.colAvgScore', 'Avg. Score')}: {quiz.averageScorePercent}%</span>
                    )}
                  </div>
                </div>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate(`/quiz/${quiz.quizCollectionId}`)}
                >
                  {t('learningPackages.startQuiz', 'Start quiz')}
                </Button>
              </div>
            ))}
          </>
        )}
        {detailItem?.mine?.questProgress?.quests?.length > 0 && (
          <>
            <Typography.Title level={5}>
              {t('learningPackages.questsTitle', 'Quests')} ({detailItem.mine.questProgress.completedQuestCount}/{detailItem.mine.questProgress.totalQuestCount})
            </Typography.Title>
            {detailItem.mine.questProgress.quests.map(quest => (
              <div key={quest.questKey} className={styles.drawerItemRow}>
                {quest.completed
                  ? <TrophyOutlined style={{ color: '#d4a017', fontSize: 16 }} />
                  : <ClockCircleOutlined style={{ color: '#d9d9d9', fontSize: 16 }} />}
                <div className={styles.drawerItemName}>
                  {quest.title}
                  <div className={styles.drawerItemMeta}>
                    {quest.timesCompleted > 0 && (
                      <span>{t('learningPackages.timesCompleted', { count: quest.timesCompleted, defaultValue: 'Completed {{count}}×' })}</span>
                    )}
                    {quest.lastCompletedAt && (
                      <span>{new Date(quest.lastCompletedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </Drawer>
    </PagesWrapper>
  );
};

export default PackagePublicDetail;
